import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import { DataGridPremium, gridClasses } from "@mui/x-data-grid-premium";

function normalize(list, prefix) {
  const arr = Array.isArray(list) ? list : [];
  return arr.map((v, i) =>
    typeof v === "string"
      ? {
          id: `${prefix}-${i + 1}-${v
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")}`,
          label: v,
        }
      : {
          id: v.id || `${prefix}-${i + 1}`,
          label: v.label || v.id || `${prefix}_${i + 1}`,
        }
  );
}

const MAPPING_TYPES = [
  { id: "direct", label: "Direct" },
  { id: "expression", label: "Derived / Expression" },
  { id: "constant", label: "Constant / Default" },
  { id: "lookup", label: "Lookup / Reference" },
  { id: "conditional", label: "Conditional / Rule-based" },
  { id: "unmapped", label: "Unmapped" },
  { id: "system", label: "System-generated" },
];

const NexusMappingEditor = ({
  sourceColumns = [],
  targetColumns = [],
  onMappingsChange,
  initialMappings = {}, // { [targetId]: { mappingType, sourceId } }
}) => {
  const sources = useMemo(
    () => normalize(sourceColumns, "SRC"),
    [sourceColumns]
  );
  const targets = useMemo(
    () => normalize(targetColumns, "TGT"),
    [targetColumns]
  );

  const sourceOptions = useMemo(
    () => sources.map((s) => ({ value: s.id, label: s.label })),
    [sources]
  );
  const mappingTypeOptions = useMemo(
    () => MAPPING_TYPES.map((t) => ({ value: t.id, label: t.label })),
    []
  );

  const initialRows = useMemo(() => {
    return targets.map((t, idx) => {
      const suggested = initialMappings?.[t.id];
      const mappingType = suggested?.mappingType || "unmapped";
      const sourceId =
        mappingType === "direct" ? suggested?.sourceId || null : null;
      const srcOpt = sourceId
        ? sources
            .map((s) => ({ value: s.id, label: s.label }))
            .find((o) => o.value === sourceId)
        : null;
      return {
        id: t.id,
        rowNo: idx + 1,
        targetField: t.label,
        targetId: t.id,
        mappingType,
        sourceId,
        sourceLabel: srcOpt?.label || "",
      };
    });
  }, [targets, initialMappings, sources]);

  const [rows, setRows] = useState(initialRows);

  // Refresh rows if targets or initialMappings change significantly
  React.useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);
  // Filters
  const [targetFilter, setTargetFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  // No inline grid header filters

  const columns = useMemo(
    () => [
      {
        field: "rowNo",
        headerName: "#",
        width: 70,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderHeaderFilter: () => null,
      },
      {
        field: "targetField",
        headerName: "Target Field",
        flex: 1,
        editable: false,
      },
      {
        field: "mappingType",
        headerName: "Mapping Type",
        width: 260,
        type: "singleSelect",
        valueOptions: mappingTypeOptions,
        editable: true,
      },
      {
        field: "sourceId",
        headerName: "Source Field",
        flex: 1,
        type: "singleSelect",
        valueOptions: sourceOptions,
        editable: (params) => params.row.mappingType === "direct",
        renderCell: (params) => {
          const opt = sourceOptions.find((o) => o.value === params.value);
          return <span>{opt?.label || ""}</span>;
        },
        getCellClassName: (params) => {
          const needsSource =
            params.row.mappingType === "direct" && !params.row.sourceId;
          return needsSource ? "mapping-error-cell" : "";
        },
      },
    ],
    [mappingTypeOptions, sourceOptions]
  );

  const processRowUpdate = useCallback(
    (newRow, oldRow) => {
      const updated = { ...newRow };
      if (updated.mappingType !== "direct") {
        updated.sourceId = null;
      }
      // attach a display label for filtering convenience
      const srcOpt = sourceOptions.find((o) => o.value === updated.sourceId);
      updated.sourceLabel = srcOpt?.label || "";
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      return updated;
    },
    [sourceOptions]
  );

  // Apply button: emit current mappings
  const handleApply = useCallback(() => {
    if (!onMappingsChange) return;
    const map = {};
    rows.forEach((r) => {
      map[r.targetId] = { mappingType: r.mappingType, sourceId: r.sourceId };
    });
    onMappingsChange(map);
  }, [onMappingsChange, rows]);

  // Filtered rows view
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const targetOk =
        !targetFilter ||
        r.targetField.toLowerCase().includes(targetFilter.toLowerCase());
      const sourceText = r.sourceLabel || "";
      const sourceOk =
        !sourceFilter ||
        sourceText.toLowerCase().includes(sourceFilter.toLowerCase());
      const typeOk = typeFilter === "all" || r.mappingType === typeFilter;
      return targetOk && sourceOk && typeOk;
    });
  }, [rows, targetFilter, sourceFilter, typeFilter]);

  return (
    <Paper
      sx={{
        width: "100%",
        height: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Mapping Editor
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            label="Filter Target"
            value={targetFilter}
            onChange={(e) => setTargetFilter(e.target.value)}
          />
          <TextField
            size="small"
            label="Filter Source"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          />
          <TextField
            size="small"
            select
            label="Mapping Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All</MenuItem>
            {MAPPING_TYPES.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          "& .mapping-error-cell": { color: "#dc2626", fontWeight: 600 },
        }}
      >
        <div style={{ height: "100%", width: "100%" }}>
          <DataGridPremium
            rows={filteredRows}
            columns={columns}
            density="compact"
            disableColumnMenu
            disableRowSelectionOnClick
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(e) => console.error(e)}
            getRowId={(r) => r.id}
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            headerFilterHeight={52}
            disableColumnFilter
            headerFilters
            slots={{
              headerFilterMenu: null,
            }}
            sx={{ [`.${gridClasses["columnHeader--filter"]}`]: { px: 1 } }}
          />
        </div>
      </Box>
    </Paper>
  );
};

export default NexusMappingEditor;
